import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { Maintenance } from './pages/Maintenance.tsx';
import { StrictMode } from 'react';
import { supabase } from './lib/supabase/client.ts';
import { enablePush, verifyNotificationSubscription } from './utils/pushSubscriptionUtils.ts';

const tryVerifyNotificationSubscription = async () => {
    const reg = await navigator.serviceWorker.ready;
    const hasValidSubscription = await verifyNotificationSubscription(reg, supabase);
    if (!hasValidSubscription) await enablePush(supabase);
};
window.addEventListener("pageshow", tryVerifyNotificationSubscription);
window.addEventListener("focus", tryVerifyNotificationSubscription);
tryVerifyNotificationSubscription();

const root = createRoot(document.getElementById("root")!);

const STATUS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/status`;

const renderApp = () => root.render(
    <StrictMode>
        <App />
    </StrictMode>
);
const renderMaintenance = () => root.render(<Maintenance />);

function render(maintenance: boolean) {
    if (maintenance) renderMaintenance();
    else renderApp();
}

async function fetchStatus(opts: RequestInit) {
    const res = await fetch(STATUS_URL, opts);
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    return data?.maintenance === true || data?.maintenance === "true";
}

function scheduleBackgroundRecheck(initial: boolean, interval = 60000) {
    let currentlyMaintenance = initial;

    // Run after first paint (or ASAP if unsupported)
    const run = async () => {

        try {
            if (localStorage.getItem("ignore-maintenance") === "true") {
                renderApp();
                return;
            }

            const maintenance = await fetchStatus({ method: "GET", cache: "no-cache" });
            if (maintenance !== currentlyMaintenance) {
                currentlyMaintenance = maintenance;
                render(maintenance);
            }
        } catch {
            // optional: if background check fails, do nothing
            // (or "fail closed" if you prefer)
            // renderMaintenance();
            // currentlyMaintenance = true;
        } finally {
            setTimeout(() => {
                if (typeof requestIdleCallback === "function") requestIdleCallback(run);
                else run();
            }, interval);
        }
    };

    // requestIdleCallback is nice, but not everywhere; fallback to setTimeout
    if (typeof requestIdleCallback === "function") requestIdleCallback(run);
    else setTimeout(run, 0);
}

(async () => {
    // 1) Initial decision (cached)
    let maintenance = false;

    try {
        maintenance = await fetchStatus({ method: "GET", cache: "force-cache" });
    } catch {
        // Your choice:
        // - fail closed -> maintenance = true
        // - fail open   -> maintenance = false
        maintenance = true;
    }

    render(maintenance);

    // 2) Background re-check (non-cached)
    scheduleBackgroundRecheck(maintenance);
})();

// Register service worker
if (import.meta.env.PROD && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js?v=1", { type: "module" });
    });
}